import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Search,
  RefreshCw,
  ExternalLink,
  Coins,
  Image as ImageIcon,
  Barcode as BarcodeIcon,
  ArrowRight,
  Archive,
} from 'lucide-react';
import {
  PageHeader,
  Panel,
  PanelBody,
  Button,
  Badge,
  Input,
  Select,
  TableShell,
  Table,
  THead,
  TBody,
  Tr,
  Th,
  Td,
  Checkbox,
  EmptyState,
  Loading,
  Stat,
  cn,
} from '../ui/index.js';
import {
  fetchMetaCommerceAudit,
  roundMetaCommercePrices,
  parkMetaCommerceStock,
  type MetaCommerceProductItem,
  type MetaCommerceIssue,
} from '../api.js';

export function MetaCommerceAuditPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [toastMessage, setToastMessage] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);

  // Fetch audit data
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['meta-commerce-audit', activeTab, searchQuery, selectedBrand, currentPage, pageSize],
    queryFn: () =>
      fetchMetaCommerceAudit({
        tab: activeTab,
        q: searchQuery.trim() || undefined,
        brand: selectedBrand !== 'all' ? selectedBrand : undefined,
        page: currentPage,
        limit: pageSize,
      }),
    staleTime: 30_000,
  });

  // Bulk Round Prices Mutation
  const roundPricesMutation = useMutation({
    mutationFn: (ids: number[]) => roundMetaCommercePrices(ids),
    onSuccess: (res) => {
      if (res.ok) {
        setToastMessage({
          text: `Succès : ${res.updatedCount} produit(s) mis à jour à 2 décimales et synchronisés sur WooCommerce.`,
          tone: 'ok',
        });
        setSelectedIds([]);
        void queryClient.invalidateQueries({ queryKey: ['meta-commerce-audit'] });
      } else {
        setToastMessage({
          text: `Arrondi partiel (${res.updatedCount} réussis). Erreurs : ${res.errors.join(' | ')}`,
          tone: 'error',
        });
        void queryClient.invalidateQueries({ queryKey: ['meta-commerce-audit'] });
      }
    },
    onError: (err: unknown) => {
      setToastMessage({
        text: `Erreur lors de la mise à jour des prix : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
  });

  // Bulk Park Stock (-999) Mutation
  const parkStockMutation = useMutation({
    mutationFn: (ids: number[]) => parkMetaCommerceStock(ids),
    onSuccess: (res) => {
      if (res.ok) {
        setToastMessage({
          text: `Succès : Stock parqué à -999 pour ${res.sent} produit(s). Ils sont immédiatement exclus des publicités Meta.`,
          tone: 'ok',
        });
        setSelectedIds([]);
        void queryClient.invalidateQueries({ queryKey: ['meta-commerce-audit'] });
      } else {
        setToastMessage({
          text: `Erreur partielle lors du masquage : ${res.failed} échec(s).`,
          tone: 'error',
        });
        void queryClient.invalidateQueries({ queryKey: ['meta-commerce-audit'] });
      }
    },
    onError: (err: unknown) => {
      setToastMessage({
        text: `Erreur lors de l'exclusion de stock : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
  });

  const counts = data?.counts;
  const items = data?.items ?? [];
  const brands = data?.brands ?? [];
  const totalPages = data?.totalPages ?? 1;

  // Toggle selection
  const toggleSelectAll = (): void => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((i) => i.id));
    }
  };

  const toggleSelectOne = (id: number): void => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleTabChange = (tabKey: string): void => {
    setActiveTab(tabKey);
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // Helper for rendering issue badges using standard Badge component
  const renderIssueBadge = (issue: MetaCommerceIssue): React.ReactElement => {
    const isError = issue.severity === 'error';
    return (
      <Badge
        key={issue.code}
        tone={isError ? 'alarm' : 'signal'}
        title={issue.description}
      >
        {isError ? (
          <XCircle className="w-3 h-3 text-rust-600 shrink-0" />
        ) : (
          <AlertTriangle className="w-3 h-3 text-clay-600 shrink-0" />
        )}
        <span>{issue.title}</span>
      </Badge>
    );
  };

  const eligibleForPriceRound = items.filter(
    (i) => selectedIds.includes(i.id) && i.issues.some((iss) => iss.code === 'PRICE_DECIMALS'),
  );

  return (
    <div className="space-y-6 pb-12">
      {/* Toast notification banner */}
      {toastMessage && (
        <div
          className={cn(
            'flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-medium',
            toastMessage.tone === 'ok'
              ? 'bg-herb-50 text-herb-800 border-herb-200'
              : 'bg-rust-100/60 text-rust-700 border-rust-300',
          )}
        >
          <span>{toastMessage.text}</span>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="text-xs font-semibold underline hover:no-underline ml-4 cursor-pointer"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Header */}
      <PageHeader
        index="S45"
        title="Conformité Meta (Facebook Ads)"
        purpose="Audit et remédiation des anomalies du catalogue Meta : décimales de prix (max 2 décimales en TND), liens images brisés ou absents, et sommes de contrôle GTIN/EAN."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isLoading || isFetching}
            className="gap-1.5"
          >
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            <span>Actualiser</span>
          </Button>
        }
      />

      {/* Standard MaPara Stat KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Total anomalies"
          value={counts?.totalWithIssues ?? 0}
          hint={`sur ${counts?.totalPublished ?? 0} produits publiés`}
          index="M1"
          tone="neutral"
          onClick={() => handleTabChange('all')}
        />
        <Stat
          label="Erreurs bloquantes (non diffusés)"
          value={counts?.criticalErrorsCount ?? 0}
          hint={`Prix manquant (${counts?.priceMissingCount ?? 0}) · Image (${counts?.imageMissingCount ?? 0})`}
          index="M2"
          tone="alarm"
          onClick={() => handleTabChange('critical')}
        />
        <Stat
          label="Avertissements qualité"
          value={counts?.qualityWarningsCount ?? 0}
          hint={`3 décimales (${counts?.priceDecimalsCount ?? 0}) · GTIN invalide (${counts?.gtinInvalidCount ?? 0})`}
          index="M3"
          tone="signal"
          onClick={() => handleTabChange('price-decimals')}
        />
        <Stat
          label="Stock parqué (-999)"
          value={counts?.parkedCount ?? 0}
          hint="Exclus immédiatement des flux Meta"
          index="M4"
          tone="muted"
          onClick={() => handleTabChange('parked')}
        />
      </div>

      {/* Main Panel */}
      <Panel>
        {/* Toolbar Header Section */}
        <div className="border-b border-border bg-card p-4 space-y-4">
          {/* Segmented Filter Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => handleTabChange('all')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer',
                activeTab === 'all'
                  ? 'bg-ink text-white shadow-xs font-semibold'
                  : 'bg-cream text-ink-soft hover:bg-sand hover:text-ink',
              )}
            >
              Tous ({counts?.totalWithIssues ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('critical')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                activeTab === 'critical'
                  ? 'bg-rust-700 text-white shadow-xs font-semibold'
                  : 'bg-rust-100/70 text-rust-700 hover:bg-rust-100 border border-rust-300/60',
              )}
            >
              <XCircle className="w-3.5 h-3.5" />
              Bloquants Meta ({counts?.criticalErrorsCount ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('price-decimals')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                activeTab === 'price-decimals'
                  ? 'bg-clay-600 text-white shadow-xs font-semibold'
                  : 'bg-clay-100/80 text-clay-700 hover:bg-clay-100 border border-clay-300/60',
              )}
            >
              <Coins className="w-3.5 h-3.5" />
              3 décimales TND ({counts?.priceDecimalsCount ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('price-missing')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer',
                activeTab === 'price-missing'
                  ? 'bg-ink text-white shadow-xs font-semibold'
                  : 'bg-cream text-ink-soft hover:bg-sand hover:text-ink',
              )}
            >
              Prix manquant ({counts?.priceMissingCount ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('image-missing')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                activeTab === 'image-missing'
                  ? 'bg-ink text-white shadow-xs font-semibold'
                  : 'bg-cream text-ink-soft hover:bg-sand hover:text-ink',
              )}
            >
              <ImageIcon className="w-3.5 h-3.5" />
              Images ({counts?.imageMissingCount ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('gtin-invalid')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                activeTab === 'gtin-invalid'
                  ? 'bg-ink text-white shadow-xs font-semibold'
                  : 'bg-cream text-ink-soft hover:bg-sand hover:text-ink',
              )}
            >
              <BarcodeIcon className="w-3.5 h-3.5" />
              GTIN invalide ({counts?.gtinInvalidCount ?? 0})
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('parked')}
              className={cn(
                'px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                activeTab === 'parked'
                  ? 'bg-ink text-white shadow-xs font-semibold'
                  : 'bg-cream text-ink-soft hover:bg-sand hover:text-ink',
              )}
            >
              <Archive className="w-3.5 h-3.5" />
              Parqués -999 ({counts?.parkedCount ?? 0})
            </button>
          </div>

          {/* Search, Brand Filter, and Bulk Actions Toolbar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-border">
            <div className="flex flex-1 items-center gap-3">
              {/* Search input */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-ink-ghost" />
                <Input
                  type="text"
                  placeholder="Rechercher par libellé, SKU, code-barres..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 text-xs"
                />
              </div>

              {/* Brand filter dropdown */}
              <div className="w-48">
                <Select
                  value={selectedBrand}
                  onChange={(e) => {
                    setSelectedBrand(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="text-xs"
                >
                  <option value="all">Toutes les marques ({brands.reduce((acc, b) => acc + b.count, 0)})</option>
                  {brands.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand} ({b.count})
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Bulk actions */}
            <div className="flex items-center gap-2">
              {selectedIds.length > 0 && (
                <>
                  <span className="text-xs font-medium text-ink-soft">
                    {selectedIds.length} sélectionné(s)
                  </span>

                  {eligibleForPriceRound.length > 0 && (
                    <Button
                      size="sm"
                      variant="primary"
                      className="text-xs gap-1"
                      disabled={roundPricesMutation.isPending}
                      onClick={() =>
                        roundPricesMutation.mutate(eligibleForPriceRound.map((p) => p.id))
                      }
                    >
                      <Coins className="w-3.5 h-3.5" />
                      <span>Arrondir prix ({eligibleForPriceRound.length})</span>
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="soft"
                    className="text-xs gap-1"
                    disabled={parkStockMutation.isPending}
                    onClick={() => parkStockMutation.mutate(selectedIds)}
                  >
                    <Archive className="w-3.5 h-3.5" />
                    <span>Park stock à -999</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <PanelBody className="p-0">
          {isLoading ? (
            <div className="p-12 flex justify-center">
              <Loading />
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <EmptyState
                title="Aucun problème de conformité trouvé"
                description={
                  searchQuery || selectedBrand !== 'all' || activeTab !== 'all'
                    ? 'Aucun produit ne correspond à vos critères de recherche actuels.'
                    : 'Félicitations, aucun produit publié ne présente d’anomalie bloquante pour Meta Commerce !'
                }
              />
            </div>
          ) : (
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th className="w-10">
                      <Checkbox
                        checked={selectedIds.length === items.length && items.length > 0}
                        onChange={toggleSelectAll}
                        aria-label="Sélectionner tous les produits"
                      />
                    </Th>
                    <Th className="w-16">Visuel</Th>
                    <Th>Produit & SKU</Th>
                    <Th className="w-48">Code GTIN / Barcode</Th>
                    <Th className="w-44 text-right">Prix TTC (TND)</Th>
                    <Th>Anomalies Meta détectées</Th>
                    <Th className="w-36 text-right">Actions</Th>
                  </Tr>
                </THead>
                <TBody>
                  {items.map((product) => {
                    const isSelected = selectedIds.includes(product.id);
                    const hasDecimalIssue = product.issues.some((i) => i.code === 'PRICE_DECIMALS');

                    return (
                      <Tr
                        key={product.id}
                        className={cn(
                          isSelected && 'bg-cream/60',
                          product.isParked && 'opacity-65',
                        )}
                      >
                        {/* Checkbox */}
                        <Td>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelectOne(product.id)}
                            aria-label={`Sélectionner ${product.name}`}
                          />
                        </Td>

                        {/* Image Thumbnail */}
                        <Td>
                          <div className="w-11 h-11 rounded-lg border border-border bg-cream/40 flex items-center justify-center overflow-hidden shrink-0">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-contain"
                                loading="lazy"
                              />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-ink-ghost" />
                            )}
                          </div>
                        </Td>

                        {/* Product Name & Brand */}
                        <Td>
                          <div className="space-y-1">
                            <div className="font-medium text-ink text-sm line-clamp-2">
                              {product.name}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-ink-soft">
                              {product.brand && (
                                <span className="font-semibold text-ink">
                                  {product.brand}
                                </span>
                              )}
                              <span>•</span>
                              <span className="font-mono text-ink-faint">SKU: {product.sku || '—'}</span>
                              <span>•</span>
                              <span className="text-ink-faint">WC #{product.wcProductId}</span>
                              {product.isVariable && (
                                <Badge tone="signal" className="text-[10px] py-0 px-1.5 font-medium">
                                  Variable ({product.variantsCount} var.)
                                </Badge>
                              )}
                              {product.isPromo && (
                                <Badge tone="ok" className="text-[10px] py-0 px-1.5 font-bold">
                                  PROMO
                                </Badge>
                              )}
                              {product.isParked && (
                                <Badge tone="muted" className="text-[10px] py-0 px-1.5">
                                  Parqué (-999)
                                </Badge>
                              )}
                            </div>
                          </div>
                        </Td>

                        {/* Barcode & GTIN Checksum */}
                        <Td>
                          {product.barcode ? (
                            <div className="space-y-0.5">
                              <span className="font-mono text-xs text-ink">
                                {product.barcode}
                              </span>
                              <div>
                                {product.gtinChecksumOk ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-herb-700 font-medium">
                                    <CheckCircle2 className="w-3 h-3 text-herb-600" />
                                    <span>GS1 Mod-10 Valide</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-rust-700 font-semibold">
                                    <XCircle className="w-3 h-3 text-rust-600" />
                                    <span>Checksum Invalide</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-ink-ghost italic">Aucun GTIN</span>
                          )}
                        </Td>

                        {/* Price TTC */}
                        <Td className="text-right">
                          <div className="space-y-0.5">
                            {product.isPromo ? (
                              <>
                                <div className="font-mono text-sm font-bold text-herb-700">
                                  {product.salePriceTndFormatted ?? product.priceTndFormatted}
                                </div>
                                {product.regularPriceTndFormatted && (
                                  <div className="font-mono text-[11px] text-ink-ghost line-through">
                                    {product.regularPriceTndFormatted}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div
                                className={cn(
                                  'font-mono text-sm font-semibold',
                                  product.priceDecimals === 3 ? 'text-clay-700' : 'text-ink',
                                )}
                              >
                                {product.priceTndFormatted}
                              </div>
                            )}
                            {hasDecimalIssue && product.suggestedRoundedPriceTnd && (
                              <div className="flex items-center justify-end gap-1 text-xs text-herb-700 font-medium font-mono pt-0.5">
                                <ArrowRight className="w-3 h-3" />
                                <span>{product.suggestedRoundedPriceTnd}</span>
                              </div>
                            )}
                          </div>
                        </Td>

                        {/* Meta Issues (Standard Badges) */}
                        <Td>
                          <div className="flex flex-wrap gap-1.5">
                            {product.issues.map((iss) => renderIssueBadge(iss))}
                          </div>
                        </Td>

                        {/* Row Actions */}
                        <Td className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Inline round price button */}
                            {hasDecimalIssue && (
                              <Button
                                size="xs"
                                variant="primary"
                                title="Arrondir à 2 décimales et synchroniser sur WooCommerce"
                                disabled={roundPricesMutation.isPending}
                                onClick={() => roundPricesMutation.mutate([product.id])}
                              >
                                Arrondir
                              </Button>
                            )}

                            {/* Inline park stock button */}
                            {!product.isParked && (
                              <Button
                                size="xs"
                                variant="soft"
                                title="Définir le stock à -999 pour exclure immédiatement le produit des publicités"
                                disabled={parkStockMutation.isPending}
                                onClick={() => parkStockMutation.mutate([product.id])}
                              >
                                -999
                              </Button>
                            )}

                            {/* Edit on WooCommerce button */}
                            <a
                              href={`https://mapara.tn/wp-admin/post.php?post=${product.wcProductId}&action=edit`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center h-6 w-6 rounded border border-border text-ink-soft hover:text-herb-700 hover:bg-cream transition-colors"
                              title="Modifier dans l'administration WooCommerce"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </TableShell>
          )}

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border text-xs text-ink-soft">
              <div>
                Page {currentPage} sur {totalPages} ({data?.total ?? 0} produits)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage <= 1 || isFetching}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="text-xs h-8"
                >
                  Précédent
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage >= totalPages || isFetching}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="text-xs h-8"
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}
